// Ô nhập một kho lấy hàng (= địa chỉ gửi hàng) — dùng chung cho Cài đặt › Kho lấy hàng và
// hộp "Thêm địa chỉ mới" trong form tạo đơn. `wording="sender"` đổi chữ cho người mới
// (Tên gợi nhớ / Người gửi / Dùng làm địa chỉ mặc định) và ẩn phần địa chỉ hoàn (mặc định hoàn về
// đúng địa chỉ gửi — sửa được ở Cài đặt).

import type { Dispatch, SetStateAction } from 'react'
import { isValidVnPhone, normalizePhoneVN } from '@/lib/format'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { SmartAddressFields } from '@/components/SmartAddressFields'
import { FieldRow } from './FieldRow'

export type Warehouse = {
  id: number
  name: string
  contactName: string
  contactPhone: string
  address: string
  city: string
  district: string | null
  ward: string
  lat: number | null
  lng: number | null
  returnSameAsPickup: boolean
  returnName: string | null
  returnPhone: string | null
  returnAddress: string | null
  returnCity: string | null
  returnDistrict: string | null
  returnWard: string | null
  isDefault: boolean
  active: boolean
}

export type WarehouseFormValue = {
  name: string
  contactName: string
  contactPhone: string
  address: string
  city: string
  district: string | null
  ward: string
  lat: number | null
  lng: number | null
  returnSameAsPickup: boolean
  returnName: string
  returnPhone: string
  returnAddress: string
  returnCity: string
  returnDistrict: string | null
  returnWard: string
  isDefault: boolean
}

export type WarehouseWording = 'warehouse' | 'sender'

export function emptyWarehouseForm(contactName: string, contactPhone: string, isFirst: boolean, wording: WarehouseWording = 'warehouse'): WarehouseFormValue {
  return {
    name: isFirst ? (wording === 'sender' ? 'Địa chỉ chính' : 'Kho chính') : '',
    contactName,
    contactPhone,
    address: '', city: '', district: null, ward: '', lat: null, lng: null,
    returnSameAsPickup: true,
    returnName: '', returnPhone: '', returnAddress: '', returnCity: '', returnDistrict: null, returnWard: '',
    isDefault: isFirst,
  }
}

export function warehouseToForm(w: Warehouse): WarehouseFormValue {
  return {
    name: w.name, contactName: w.contactName ?? '', contactPhone: w.contactPhone ?? '',
    address: w.address ?? '', city: w.city ?? '', district: w.district, ward: w.ward ?? '', lat: w.lat, lng: w.lng,
    returnSameAsPickup: w.returnSameAsPickup,
    returnName: w.returnName ?? '', returnPhone: w.returnPhone ?? '', returnAddress: w.returnAddress ?? '',
    returnCity: w.returnCity ?? '', returnDistrict: w.returnDistrict, returnWard: w.returnWard ?? '',
    isDefault: w.isDefault,
  }
}

export const fullAddress = (address: string, ward: string, district: string | null, city: string) =>
  [address, ward, district, city].filter((p) => p && p.trim()).join(', ')

/** Kiểm tra + dựng body cho POST/PUT /settings/warehouses. Ném Error (thông báo tiếng Việt) nếu thiếu. */
export function warehousePayload(form: WarehouseFormValue, wording: WarehouseWording = 'warehouse') {
  const sender = wording === 'sender'
  if (!form.name.trim()) throw new Error(sender ? 'Vui lòng đặt tên gợi nhớ (VD: Nhà riêng, Cửa hàng)' : 'Vui lòng đặt tên kho (VD: Kho Cầu Giấy)')
  if (!form.contactName.trim()) throw new Error(sender ? 'Vui lòng nhập tên người gửi' : 'Vui lòng nhập tên người liên hệ')
  if (!isValidVnPhone(form.contactPhone)) throw new Error(sender ? 'Số điện thoại người gửi không đúng định dạng di động VN' : 'Số điện thoại liên hệ không đúng định dạng di động VN')
  if (!form.address.trim()) throw new Error('Vui lòng nhập số nhà, tên đường')
  if (!form.city.trim() || !form.ward.trim()) throw new Error(sender ? 'Vui lòng chọn Tỉnh/Thành phố và Phường/Xã gửi hàng' : 'Vui lòng chọn Tỉnh/Thành phố và Phường/Xã lấy hàng')
  if (!form.returnSameAsPickup) {
    if (!form.returnAddress.trim() || !form.returnCity.trim() || !form.returnWard.trim()) {
      throw new Error('Vui lòng nhập đủ địa chỉ hoàn hàng (số nhà, Phường/Xã, Tỉnh/Thành phố)')
    }
    if (form.returnPhone && !isValidVnPhone(form.returnPhone)) throw new Error('Số điện thoại nhận hàng hoàn không đúng định dạng')
  }
  return {
    name: form.name.trim(),
    contactName: form.contactName.trim(),
    contactPhone: normalizePhoneVN(form.contactPhone),
    address: form.address.trim(),
    city: form.city.trim(),
    district: form.district?.trim() || null,
    ward: form.ward.trim(),
    lat: form.lat,
    lng: form.lng,
    returnSameAsPickup: form.returnSameAsPickup,
    ...(form.returnSameAsPickup
      ? {}
      : {
          returnName: form.returnName.trim() || null,
          returnPhone: form.returnPhone ? normalizePhoneVN(form.returnPhone) : null,
          returnAddress: form.returnAddress.trim(),
          returnCity: form.returnCity.trim(),
          returnDistrict: form.returnDistrict?.trim() || null,
          returnWard: form.returnWard.trim(),
        }),
    isDefault: form.isDefault,
  }
}

/** Các ô nhập — KHÔNG có thẻ <form> (hộp trong form tạo đơn không được lồng form). */
export function WarehouseFormFields({
  form,
  setForm,
  disabled,
  showDefault,
  wording = 'warehouse',
}: {
  form: WarehouseFormValue
  setForm: Dispatch<SetStateAction<WarehouseFormValue>>
  disabled?: boolean
  /** Hiện ô "mặc định" (ẩn khi đây là địa chỉ đầu tiên — tự là mặc định). */
  showDefault: boolean
  wording?: WarehouseWording
}) {
  const sender = wording === 'sender'
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <FieldRow label={sender ? 'Tên gợi nhớ' : 'Tên kho'}>
          <Input required placeholder={sender ? 'VD: Nhà riêng, Cửa hàng' : 'VD: Kho Cầu Giấy'} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} disabled={disabled} />
        </FieldRow>
        <FieldRow label={sender ? 'Người gửi' : 'Người liên hệ'}>
          <Input required value={form.contactName} onChange={(e) => setForm((f) => ({ ...f, contactName: e.target.value }))} disabled={disabled} />
        </FieldRow>
        <FieldRow label="Số điện thoại">
          <PhoneInput
            required
            placeholder="0987 654 321"
            value={form.contactPhone}
            onValueChange={(val) => setForm((f) => ({ ...f, contactPhone: val || '' }))}
            showValidation={true}
          />
        </FieldRow>
      </div>

      <SmartAddressFields
        value={{ senderAddress: form.address, senderCity: form.city, senderDistrict: form.district, senderWard: form.ward, lat: form.lat, lng: form.lng }}
        onChange={(p) =>
          setForm((prev) => ({
            ...prev,
            address: p.senderAddress ?? prev.address,
            city: p.senderCity ?? prev.city,
            district: p.senderDistrict !== undefined ? p.senderDistrict || null : prev.district,
            ward: p.senderWard ?? prev.ward,
            lat: p.lat !== undefined ? p.lat ?? null : prev.lat,
            lng: p.lng !== undefined ? p.lng ?? null : prev.lng,
          }))
        }
        disabled={disabled}
      />

      {!sender && (
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={!form.returnSameAsPickup} onChange={(e) => setForm((f) => ({ ...f, returnSameAsPickup: !e.target.checked }))} />
          Hàng hoàn trả về địa chỉ khác
        </label>
      )}

      {!sender && !form.returnSameAsPickup && (
        <div className="space-y-4 rounded-lg border border-rule p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FieldRow label="Người nhận hàng hoàn">
              <Input value={form.returnName} onChange={(e) => setForm((f) => ({ ...f, returnName: e.target.value }))} />
            </FieldRow>
            <FieldRow label="Số điện thoại nhận hàng hoàn">
              <PhoneInput value={form.returnPhone} onValueChange={(val) => setForm((f) => ({ ...f, returnPhone: val || '' }))} />
            </FieldRow>
          </div>
          <SmartAddressFields
            value={{ senderAddress: form.returnAddress, senderCity: form.returnCity, senderDistrict: form.returnDistrict, senderWard: form.returnWard }}
            onChange={(p) =>
              setForm((prev) => ({
                ...prev,
                returnAddress: p.senderAddress ?? prev.returnAddress,
                returnCity: p.senderCity ?? prev.returnCity,
                returnDistrict: p.senderDistrict !== undefined ? p.senderDistrict || null : prev.returnDistrict,
                returnWard: p.senderWard ?? prev.returnWard,
              }))
            }
            disabled={disabled}
          />
        </div>
      )}

      {showDefault && (
        <label className="flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm((f) => ({ ...f, isDefault: e.target.checked }))} />
          {sender ? 'Dùng làm địa chỉ mặc định' : 'Đặt làm kho mặc định'}
        </label>
      )}
    </div>
  )
}
